import { useEffect, useRef } from 'react';
import { CheckIcon, CopyIcon } from '@/components/icons';
import { useClipboard } from '@/hooks/useClipboard';
import './ConfigViewer.css';

interface ConfigViewerProps {
  /** The configuration content to display */
  content: string;
  /** Title for the viewer */
  title?: string;
  /** Optional max height for the viewer (default: 400px) */
  maxHeight?: string;
  /** Whether to show line numbers (default: true) */
  showLineNumbers?: boolean;
  /** Callback when copy button is clicked */
  onCopy?: () => void;
}

/**
 * ConfigViewer component for displaying NGINX configuration content
 *
 * Features:
 * - Syntax-like formatting for nginx config
 * - Line numbers
 * - Copy to clipboard
 * - Responsive scrolling
 */
export function ConfigViewer({
  content,
  title,
  maxHeight = '400px',
  showLineNumbers = true,
  onCopy,
}: ConfigViewerProps) {
  const { copiedKey, copy } = useClipboard();
  const copied = copiedKey !== null;
  const preRef = useRef<HTMLPreElement>(null);

  // Split content into lines for line numbering
  const lines = content.split('\n');

  /**
   * Copy content to clipboard (with a fallback for non-secure contexts)
   */
  const handleCopy = async () => {
    if (await copy(content)) {
      onCopy?.();
    }
  };

  // Auto-scroll to top when content changes
  useEffect(() => {
    if (preRef.current) {
      preRef.current.scrollTop = 0;
    }
  }, [content]);

  return (
    <div className="config-viewer">
      {title && (
        <div className="config-viewer-header">
          <span className="config-viewer-title">{title}</span>
          <button
            type="button"
            className="config-viewer-copy"
            onClick={handleCopy}
            title={copied ? 'Copied!' : 'Copy to clipboard'}
            aria-label="Copy configuration to clipboard"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>
      )}
      <div
        className="config-viewer-content"
        style={{ maxHeight }}
      >
        {!title && (
          <button
            type="button"
            className="config-viewer-copy config-viewer-copy-floating"
            onClick={handleCopy}
            title={copied ? 'Copied!' : 'Copy to clipboard'}
            aria-label="Copy configuration to clipboard"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
        )}
        <pre ref={preRef} className="config-viewer-pre">
          <code className="config-viewer-code">
            {lines.map((line, index) => (
              <div key={index} className="config-viewer-line">
                {showLineNumbers && (
                  <span className="config-viewer-line-number">
                    {index + 1}
                  </span>
                )}
                <span className="config-viewer-line-content">
                  {highlightNginxSyntax(line)}
                </span>
              </div>
            ))}
          </code>
        </pre>
      </div>
    </div>
  );
}

/**
 * Basic NGINX syntax highlighting
 */
function highlightNginxSyntax(line: string): React.ReactNode {
  // Handle comments
  if (line.trim().startsWith('#')) {
    return <span className="config-syntax-comment">{line}</span>;
  }

  // Handle directives (words followed by space and value)
  const directiveMatch = line.match(/^(\s*)([\w_]+)(\s+)(.*)$/);
  if (directiveMatch) {
    const [, indent, directive, space, rest] = directiveMatch;
    return (
      <>
        {indent}
        <span className="config-syntax-directive">{directive}</span>
        {space}
        <span className="config-syntax-value">{highlightValue(rest)}</span>
      </>
    );
  }

  // Handle blocks (opening/closing braces)
  if (line.includes('{') || line.includes('}')) {
    return highlightBraces(line);
  }

  return line;
}

/**
 * Highlight values in nginx config
 */
function highlightValue(value: string): React.ReactNode {
  // Highlight strings in quotes
  const parts = value.split(/(["'][^"']*["'])/);

  return parts.map((part, i) => {
    if (part.startsWith('"') || part.startsWith("'")) {
      return <span key={i} className="config-syntax-string">{part}</span>;
    }
    // Highlight numbers
    const withNumbers = part.split(/(\b\d+\b)/);
    return withNumbers.map((subpart, j) => {
      if (/^\d+$/.test(subpart)) {
        return <span key={`${i}-${j}`} className="config-syntax-number">{subpart}</span>;
      }
      // Highlight variables
      if (subpart.includes('$')) {
        const varParts = subpart.split(/(\$[\w_]+)/);
        return varParts.map((vp, k) => {
          if (vp.startsWith('$')) {
            return <span key={`${i}-${j}-${k}`} className="config-syntax-variable">{vp}</span>;
          }
          return vp;
        });
      }
      return subpart;
    });
  });
}

/**
 * Highlight braces in nginx config
 */
function highlightBraces(line: string): React.ReactNode {
  const parts = line.split(/([{}])/);
  return parts.map((part, i) => {
    if (part === '{' || part === '}') {
      return <span key={i} className="config-syntax-brace">{part}</span>;
    }
    // Check if it's a block name
    if (i === 0 && part.trim()) {
      return <span key={i} className="config-syntax-block">{part}</span>;
    }
    return part;
  });
}
